import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { can, useOrg } from "../org";
import { Field, Select } from "./bits";

export const RequestDialog = ({ onClose }) => {
  const { api } = useOrg();
  const [f, setF] = useState({ first_name: "", last_name: "", contact: "", note: "" });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const send = async () => {
    try {
      await api("/person-requests", { method: "POST", body: f });
      toast.success("Request sent", { description: "A pastor will add them or point you to their existing record." });
      onClose(true);
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="bg-paper max-w-md" data-testid="request-dialog">
        <DialogTitle className="t-name-lg">Ask to have them added</DialogTitle>
        <DialogDescription className="t-meta">A pastor checks for duplicates before adding anyone.</DialogDescription>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="First name"><input value={f.first_name} onChange={set("first_name")} className="field h-12 px-3" data-testid="request-first" /></Field>
          <Field label="Last name"><input value={f.last_name} onChange={set("last_name")} className="field h-12 px-3" data-testid="request-last" /></Field>
        </div>
        <Field label="How to reach them (optional)"><input value={f.contact} onChange={set("contact")} className="field h-12 px-3" data-testid="request-contact" /></Field>
        <Field label="Why they need care (optional)"><textarea value={f.note} onChange={set("note")} rows={3} className="field p-3" data-testid="request-note" /></Field>
        <button disabled={!f.first_name.trim() || !f.last_name.trim()} onClick={send} className="btn btn-primary w-full mt-2" data-testid="request-send">Send request</button>
      </DialogContent>
    </Dialog>
  );
};

const ResolveRow = ({ r, people, onDone }) => {
  const { api, membership } = useOrg();
  const [existing, setExisting] = useState(null);
  const [assign, setAssign] = useState(r.requester?.role !== "admin");
  const isCare = can(membership).care;
  const go = (action) => api(`/person-requests/${r.id}/resolve`, { method: "POST", body: { action, person_id: existing, assign_requester: isCare && assign && action !== "decline" } })
    .then(() => { toast.success(action === "decline" ? "Request declined" : "Done"); onDone(); }).catch((e) => toast.error(e.message));
  return (
    <div className="py-5" data-testid={`request-${r.id}`}>
      <p className="t-name-sm">{r.first_name} {r.last_name}</p>
      <p className="t-meta">Asked by {r.requester?.name}{r.contact ? ` · ${r.contact}` : ""}</p>
      {r.note && <p className="t-body mt-2">{r.note}</p>}
      {isCare && <label className="flex items-center gap-3 mt-3 min-h-[44px] t-meta cursor-pointer"><input type="checkbox" className="w-5 h-5 accent-[var(--moss)]" checked={assign} onChange={(e) => setAssign(e.target.checked)} data-testid={`request-assign-${r.id}`} /> Make {r.requester?.name?.split(" ")[0]} their Assigned Care Leader</label>}
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={() => go("add")} className="btn btn-primary btn-sm" data-testid={`request-add-${r.id}`}>Add as new person</button>
        <Select value={existing} onChange={setExisting} testid={`request-existing-${r.id}`} className="!h-11 !w-auto max-w-[220px]">
          <option value="">Already in Care?</option>
          {people.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
        </Select>
        {existing && <button onClick={() => go("existing")} className="btn btn-outline btn-sm" data-testid={`request-link-${r.id}`}>Use this record</button>}
        <button onClick={() => go("decline")} className="btn btn-quiet btn-sm" data-testid={`request-decline-${r.id}`}>Decline</button>
      </div>
    </div>
  );
};

export const RequestsPanel = ({ people, onChange }) => {
  const { api, membership } = useOrg();
  const [reqs, setReqs] = useState([]);
  const load = () => api("/person-requests").then(setReqs).catch(() => {});
  useEffect(() => { load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  const directory = can(membership).directory;
  const shown = directory ? reqs.filter((r) => r.status === "pending") : reqs;
  if (!shown.length) return null;
  return (
    <section className="mt-10 border-l-2 border-sun pl-5" data-testid="requests-panel">
      <h2 className="t-heading-sm">{directory ? `${shown.length} ${shown.length === 1 ? "person" : "people"} waiting to be added` : "Your requests"}</h2>
      <div className="rule-list">
        {directory ? shown.map((r) => <ResolveRow key={r.id} r={r} people={people} onDone={() => { load(); onChange(); }} />) : shown.map((r) => (
          <div key={r.id} className="py-3 flex justify-between gap-3" data-testid={`my-request-${r.id}`}>
            <p className="t-body">{r.first_name} {r.last_name}</p>
            <span className={`status ${r.status === "pending" ? "status-attention" : r.status === "added" ? "status-active" : "status-neutral"}`}>{r.status === "pending" ? "Waiting" : r.status === "added" ? "Added" : "Declined"}</span>
          </div>
        ))}
      </div>
    </section>
  );
};
